import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import aviso, { textoDoErro } from '../lib/aviso';
import {
    LuPlus, LuSearch, LuPencil, LuEye, LuTrash2, LuChevronLeft, LuChevronRight,
    LuBuilding2, LuUsers, LuLifeBuoy, LuTriangleAlert, LuToggleRight, LuHistory, LuRocket,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import { ListRow, PerPage, RowAction, RowActions, ListCard, ListCards } from '../components/ListKit';
import { useCelular } from '../hooks/useCelular';
import { apiFetch } from '../services/api';
import Select from '../components/Select';
import '../components/ListKit.css';
import { useRecorte } from '../hooks/useRecorte';
import './EmpresasPage.css';
import {
    EmpresaService,
    paraEmpresaUsuario,
    type Empresa, type EmpresaUsuario, type AdministradorAtivo,
} from '../services/EmpresaService';
import type { Profile } from '../types';
import {
    FuncionalidadesSistemaService, acessoDaEmpresa, type FuncionalidadesLista,
} from '../services/FuncionalidadesSistemaService';
import './FuncionalidadesSistemaPage.css';

const apiUrl = import.meta.env.VITE_API_URL;
const POR_PAGINA = [25, 50, 100];

type Modo = 'ver' | 'editar';
type Aba = 'dados' | 'usuarios' | 'suporte' | 'funcionalidades' | 'historico';

const soDigitos = (v: string) => (v || '').replace(/\D/g, '').slice(0, 14);

const comMascaraCnpj = (v: string) => {
    const d = soDigitos(v);
    if (!d) return '';
    return d
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
};

const dataCurta = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString('pt-BR') : null;

/* O campo date quer o dia local. A API guarda o fim do dia de Brasília em UTC
   (15/10 vira 16/10 02:59:59Z), então cortar a ISO mostraria o dia seguinte. */
const dataDoCampo = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso); if (isNaN(d.getTime())) return '';
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** Quantos dias faltam para a assinatura expirar. Negativo = já expirou. */
const diasAte = (iso?: string | null) => {
    if (!iso) return null;
    const alvo = new Date(iso); if (isNaN(alvo.getTime())) return null;
    const hoje = new Date();
    alvo.setHours(0, 0, 0, 0); hoje.setHours(0, 0, 0, 0);
    return Math.round((alvo.getTime() - hoje.getTime()) / 86400000);
};

/* Celular do cadastro, gravado como "5541999990001": volta a "(41) 99999-0001". */
/* "2026-10-28T20:15:00Z" → "28/10": a data da marca de boas-vindas (ISO com Z, então `new Date` acerta o fuso). */
const formatarDataCurta = (iso: string) =>
    new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

const formatarTelefoneBr = (v: string) => {
    const d = v.replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '');
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return v;
};

const iniciais = (nome: string) => (nome || '').trim().split(/\s+/).slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? '').join('') || '?';

interface RascunhoEmpresa { id: number | null; nome: string; cnpj: string; expira: string }
const EMPRESA_VAZIA: RascunhoEmpresa = { id: null, nome: '', cnpj: '', expira: '' };

/* Sem senha desde 05/10/2026: o usuário é convidado por e-mail e cria a própria. */
interface RascunhoUsuario {
    usuarioId: number | null; nome: string; email: string;
    tipoUsuarioId: number; ativo: boolean;
}
const USUARIO_VAZIO: RascunhoUsuario = {
    usuarioId: null, nome: '', email: '', tipoUsuarioId: 0, ativo: true,
};

const EmpresasPage = () => {
    const celular = useCelular();
    const [empresas, setEmpresas] = useState<Empresa[]>([]);
    const [perfis, setPerfis] = useState<Profile[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    /* Recorte de trabalho, na sessao da aba. (A `aba` logo abaixo e do painel
       do registro, nao da lista: essa nao se guarda.) */
    const [recorte, gravarRecorte] = useRecorte('emp:recorte', {
        busca: '', pagina: 1, porPagina: 25,
    });
    const [busca, setBusca] = useState(recorte.busca);
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    useEffect(() => {
        gravarRecorte({ busca, pagina, porPagina });
    }, [busca, pagina, porPagina, gravarRecorte]);

    const [aberto, setAberto] = useState<Empresa | null>(null);
    const [modo, setModo] = useState<Modo>('ver');
    const [aba, setAba] = useState<Aba>('dados');
    const [rascunho, setRascunho] = useState<RascunhoEmpresa>(EMPRESA_VAZIA);
    const [salvando, setSalvando] = useState(false);
    const [erros, setErros] = useState<Record<string, string>>({});

    /* Usuários da empresa aberta — carregados só quando a aba abre. */
    const [usuarios, setUsuarios] = useState<EmpresaUsuario[]>([]);
    const [carregandoUsuarios, setCarregandoUsuarios] = useState(false);
    const [usuarioEmEdicao, setUsuarioEmEdicao] = useState<number | 'novo' | null>(null);
    const [rascunhoUsuario, setRascunhoUsuario] = useState<RascunhoUsuario>(USUARIO_VAZIO);
    const [salvandoUsuario, setSalvandoUsuario] = useState(false);

    /* Assumir controle: só administradores ATIVOS podem ser assumidos. */
    const [admins, setAdmins] = useState<AdministradorAtivo[]>([]);
    /* Aba Funcionalidades: só lê. Para mudar, o nome leva à tela de Sistema ›
       Funcionalidades com o painel daquela funcionalidade aberto — um lugar só
       onde se muda uma exceção (fase-6-tela.html §4). */
    const [funcionalidades, setFuncionalidades] = useState<FuncionalidadesLista | null>(null);
    const [carregandoFuncionalidades, setCarregandoFuncionalidades] = useState(false);
    const navigate = useNavigate();
    const [carregandoAdmins, setCarregandoAdmins] = useState(false);
    const [assumindo, setAssumindo] = useState(false);

    /* ─── Dados ──────────────────────────────────────────────── */

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const r = await EmpresaService.buscarTodas();
            if (r.sucesso) setEmpresas(r.dados || []);
            else aviso.erro(r.mensagem || 'Não foi possível carregar as empresas.');
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível carregar as empresas.'));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        carregar();
        apiFetch(`${apiUrl}/api/TipoUsuario/BuscarTodos`)
            .then((r: Response) => r.json())
            .then(d => { if (d.sucesso) setPerfis(d.dados || []); })
            .catch(() => { });
    }, [carregar]);

    // Mudar um filtro volta para a pagina 1 - mas nao na montagem, senao a
    // pagina restaurada da sessao se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, porPagina]);

    /* Da mais antiga para a mais nova, pelo cadastro. Sem data vai para o fim;
       empate (ou as duas sem data) desempata pelo id, que também cresce com o
       cadastro. */
    const ordenadas = useMemo(() => {
        const quando = (e: Empresa) => {
            const t = e.dataCadastro ? new Date(e.dataCadastro).getTime() : NaN;
            return isNaN(t) ? Infinity : t;
        };
        return [...empresas].sort((a, b) => (quando(a) - quando(b)) || (a.id - b.id));
    }, [empresas]);

    const filtradas = useMemo(() => {
        const t = busca.trim().toLowerCase();
        if (!t) return ordenadas;
        const digitos = t.replace(/\D/g, '');
        return ordenadas.filter(e =>
            (e.nome || '').toLowerCase().includes(t)
            || (e.administrador || '').toLowerCase().includes(t)
            || (digitos && soDigitos(e.cnpj).includes(digitos)));
    }, [ordenadas, busca]);

    const totalPaginas = Math.max(1, Math.ceil(filtradas.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtradas.slice(inicio, inicio + porPagina);

    /* ─── Painel ─────────────────────────────────────────────── */

    const abrirVer = (e: Empresa) => {
        setAberto(e); setModo('ver'); setAba('dados'); setErros({});
        setUsuarioEmEdicao(null);
    };

    const abrirEdicao = (e: Empresa) => {
        setAberto(e);
        setRascunho({
            id: e.id, nome: e.nome ?? '', cnpj: e.cnpj ?? '',
            expira: dataDoCampo(e.dataAssinaturaExpira),
        });
        setModo('editar'); setAba('dados'); setErros({});
    };

    const abrirNova = () => {
        setAberto({ id: 0, nome: '', cnpj: '', dataAssinaturaExpira: '' } as Empresa);
        setRascunho(EMPRESA_VAZIA); setModo('editar'); setAba('dados'); setErros({});
    };

    const fechar = () => {
        setAberto(null); setErros({}); setUsuarios([]); setAdmins([]);
        setUsuarioEmEdicao(null);
    };

    const ehNova = modo === 'editar' && rascunho.id === null;

    const salvar = async () => {
        const e: Record<string, string> = {};
        if (!rascunho.nome.trim()) e.nome = 'Informe o nome da empresa.';
        const cnpj = soDigitos(rascunho.cnpj);
        if (cnpj && cnpj.length !== 14) e.cnpj = 'O CNPJ precisa ter 14 dígitos.';
        if (Object.keys(e).length) { setErros(e); return; }

        setSalvando(true);
        try {
            // Vazio vai como null: "" não desserializa em DateTime? e a API devolve 400.
            const dto = { nome: rascunho.nome.trim(), cnpj, dataAssinaturaExpira: rascunho.expira || null };
            const r = rascunho.id
                ? await EmpresaService.atualizar({ id: rascunho.id, ...dto })
                : await EmpresaService.criar(dto);
            if (r.sucesso) {
                aviso.ok(rascunho.id ? 'Empresa atualizada.' : 'Empresa criada.');
                await carregar(); fechar();
            } else aviso.erro(r.mensagem || 'Não foi possível salvar a empresa.');
        } catch (err: any) {
            aviso.erro(textoDoErro(err, 'Não foi possível salvar a empresa.'));
        } finally {
            setSalvando(false);
        }
    };

    /* `alvo` opcional porque a desativação tem duas portas: a lixeira da linha
       (que manda a empresa) e o rodapé do painel (que usa o que está em
       edição). A lixeira aqui desativa, não apaga — é soft delete, e foi
       decisão dele manter o mesmo ícone das outras telas. */
    const desativar = async (alvo?: Empresa) => {
        const id = alvo?.id ?? rascunho.id;
        const nome = alvo?.nome ?? rascunho.nome;
        if (!id) return;
        if (!confirm(`Desativar "${nome}"? Os usuários dela perdem o acesso.`)) return;
        setSalvando(true);
        try {
            const r = await EmpresaService.desativar(id);
            if (r.sucesso) { aviso.ok('Empresa desativada.'); await carregar(); fechar(); }
            else aviso.erro(r.mensagem || 'Não foi possível desativar a empresa.');
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível desativar a empresa.'));
        } finally {
            setSalvando(false);
        }
    };

    /* ─── Usuários da empresa ────────────────────────────────── */

    const carregarUsuarios = useCallback(async (empresaId: number) => {
        setCarregandoUsuarios(true);
        try {
            const r = await EmpresaService.buscarPorId(empresaId);
            if (r.sucesso) setUsuarios((r.dados?.usuarios || []).map(paraEmpresaUsuario));
        } catch { /* silencioso: a aba mostra o vazio */ }
        finally { setCarregandoUsuarios(false); }
    }, []);

    useEffect(() => {
        if (aba === 'usuarios' && aberto?.id) carregarUsuarios(aberto.id);
    }, [aba, aberto?.id, carregarUsuarios]);

    useEffect(() => {
        if (aba !== 'funcionalidades' || !aberto?.id) return;
        setCarregandoFuncionalidades(true);
        FuncionalidadesSistemaService.listar()
            .then(setFuncionalidades)
            .catch(() => setFuncionalidades(null))
            .finally(() => setCarregandoFuncionalidades(false));
    }, [aba, aberto?.id]);

    useEffect(() => {
        if (aba !== 'suporte' || !aberto?.id) return;
        setCarregandoAdmins(true);
        EmpresaService.buscarAdministradoresAtivos(aberto.id)
            .then(r => setAdmins(r.sucesso ? (r.dados || []) : []))
            .catch(() => setAdmins([]))
            .finally(() => setCarregandoAdmins(false));
    }, [aba, aberto?.id]);

    const abrirUsuario = (u?: EmpresaUsuario) => {
        setUsuarioEmEdicao(u ? u.usuarioId : 'novo');
        setRascunhoUsuario(u
            ? { usuarioId: u.usuarioId, nome: u.nome, email: u.email, tipoUsuarioId: u.tipoUsuarioId, ativo: u.ativo }
            : { ...USUARIO_VAZIO, tipoUsuarioId: perfis[0]?.id ?? 0 });
    };

    const salvarUsuario = async () => {
        if (!aberto?.id) return;
        const { nome, email, tipoUsuarioId, usuarioId } = rascunhoUsuario;
        if (!nome.trim() || !email.trim()) { aviso.atencao('Nome e e-mail são obrigatórios.'); return; }
        if (!tipoUsuarioId) { aviso.atencao('Escolha o perfil.'); return; }

        setSalvandoUsuario(true);
        try {
            const r = usuarioId
                ? await EmpresaService.atualizarUsuario({
                    usuarioId, empresaId: aberto.id, nome: nome.trim(),
                    email: email.trim(), tipoUsuarioId, ativo: rascunhoUsuario.ativo,
                })
                : await EmpresaService.criarUsuario({
                    empresaId: aberto.id, nome: nome.trim(),
                    email: email.trim(), tipoUsuarioId,
                });
            if (r.sucesso) {
                aviso.ok(usuarioId ? 'Usuário atualizado.' : (r.mensagem || `Convite enviado para ${email.trim()}.`));
                setUsuarioEmEdicao(null);
                await carregarUsuarios(aberto.id);
            } else aviso.erro(r.mensagem || 'Não foi possível salvar o usuário.');
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível salvar o usuário.'));
        } finally {
            setSalvandoUsuario(false);
        }
    };

    /* ─── Assumir controle ───────────────────────────────────── */

    const assumir = async (admin: AdministradorAtivo) => {
        if (!aberto?.id) return;
        if (!confirm(`Entrar em "${aberto.nome}" como ${admin.userName}? Sua sessão atual é trocada.`)) return;
        setAssumindo(true);
        try {
            const r = await EmpresaService.assumirControle({ empresaId: aberto.id, usuarioId: admin.userId });
            if (!r.sucesso) throw new Error(r.mensagem || 'Falha ao assumir o controle.');

            // A sessão trocou no servidor: relê o usuário antes de navegar.
            const me = await fetch(`${apiUrl}/api/Auth/Me`, { credentials: 'include' });
            if (!me.ok) throw new Error('Falha ao carregar a sessão de suporte.');
            localStorage.setItem('user', JSON.stringify(await me.json()));

            aviso.ok(`Você está em "${aberto.nome}".`);
            window.location.href = '/pipeline';
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível assumir o controle.'));
            setAssumindo(false);
        }
    };

    const nomePerfil = (id: number) => perfis.find(p => p.id === id)?.nome ?? '—';

    /* O Editar mora DENTRO da aba, não acima dela (2026-09-12). Só na aba
       "Dados": "Usuários" e "Suporte" têm ações próprias e não se editam por
       este formulário — era justamente isso que o botão acima das abas
       prometia sem cumprir. */
    const botaoEditar = aberto && aberto.id && modo === 'ver' && (
        <div className="rp-actions is-inline">
            <button type="button" className="rp-btn is-primary" onClick={() => abrirEdicao(aberto)}>
                <LuPencil size={13} /> Editar
            </button>
        </div>
    );

    const COLUNAS = '1.5fr 1.2fr 1.1fr 1.1fr .8fr 1.1fr .8fr 78px';

    /* O aviso de "nada aqui" e o rodapé de paginação servem à tabela (no
       computador) e aos cartões (no celular) — por isso saem do meio do JSX. */
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {empresas.length === 0 ? 'Nenhuma empresa' : 'Nada com esse termo'}
            </div>
            <p className="lk-blank-text">
                {empresas.length === 0
                    ? 'As empresas que usam a plataforma aparecem aqui.'
                    : 'Ajuste a busca para ver as outras.'}
            </p>
            {empresas.length === 0 && (
                <button type="button" className="lk-btn is-primary" onClick={abrirNova}>
                    <LuPlus size={14} /> Nova empresa
                </button>
            )}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, filtradas.length)} de {filtradas.length}
            </span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} />
                </button>
            </span>
        </div>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Empresas"
                subtitle="Todas as empresas da plataforma."
                /* Lista vazia: a porta é a do aviso, no meio da tela. */
                actions={!isLoading && empresas.length === 0 ? undefined :
                    <div className="lk-actions">
                        <button type="button" className="lk-btn is-primary" onClick={abrirNova}>
                            <LuPlus size={14} /> Nova empresa
                        </button>
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar por nome, administrador ou CNPJ…"
                            value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>
                    <span className="lk-count">
                        {empresas.length} {empresas.length === 1 ? 'empresa' : 'empresas'}
                    </span>
                </div>

                {/* Celular: a grade de cinco colunas não cabe em 358px, então a
                    linha vira cartão — quem é a empresa, o CNPJ que a identifica
                    e em que pé está a assinatura. O resto fica no painel. */}
                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(e => {
                                    const dias = diasAte(e.dataAssinaturaExpira);
                                    const data = dataCurta(e.dataAssinaturaExpira);
                                    return (
                                        <ListCard
                                            key={e.id}
                                            avatar={<span className="lk-ava" aria-hidden="true">{iniciais(e.nome)}</span>}
                                            titulo={e.nome}
                                            apoio={[e.administrador, comMascaraCnpj(e.cnpj) || 'sem CNPJ'].filter(Boolean).join(' · ')}
                                            estado={
                                                <>
                                                    <span className={`rp-badge ${e.ativo === false ? 'is-lost' : 'is-good'}`}>
                                                        {e.ativo === false ? 'Inativa' : 'Ativa'}
                                                    </span>
                                                    {!data
                                                        ? <span>· sem data</span>
                                                        : <span className={dias !== null && dias < 0 ? 'em-vencida' : undefined}>
                                                            · {data}
                                                            {dias !== null && dias >= 0 && dias <= 30 && (
                                                                <span className="em-prazo"> · {dias}d</span>
                                                            )}
                                                            {dias !== null && dias < 0 && <span> · vencida</span>}
                                                          </span>}
                                                </>
                                            }
                                            selected={aberto?.id === e.id}
                                            label={`Abrir ${e.nome}`}
                                            onOpen={() => abrirVer(e)}
                                            acoes={
                                                <RowAction icon={<LuTrash2 size={15} />} label="Desativar empresa" danger
                                                    onClick={() => desativar(e)} />
                                            }
                                        />
                                    );
                                })}
                        </ListCards>
                        {!isLoading && filtradas.length === 0 && listaVazia}
                        {!isLoading && filtradas.length > 0 && rodape}
                    </>
                ) : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Empresa</div>
                            <div className="lk-head">Administrador</div>
                            <div className="lk-head">Telefone</div>
                            <div className="lk-head">CNPJ</div>
                            <div className="lk-head">Cadastro</div>
                            <div className="lk-head">Assinatura</div>
                            <div className="lk-head">Status</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>
                        <div className="lk-body">
                            {isLoading
                                ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(e => {
                                    const dias = diasAte(e.dataAssinaturaExpira);
                                    const data = dataCurta(e.dataAssinaturaExpira);
                                    return (
                                        <ListRow key={e.id} columns={COLUNAS}
                                            selected={aberto?.id === e.id}>
                                            <span className="lk-cell lk-strong"><span>{e.nome}</span></span>
                                            <span className={`lk-cell ${e.administrador ? '' : 'lk-empty-value'}`}>
                                                <span>{e.administrador || 'sem administrador'}</span>
                                            </span>
                                            <span className={`lk-cell ${e.administradorTelefone ? 'lk-muted' : 'lk-empty-value'}`}>
                                                <span>{e.administradorTelefone ? formatarTelefoneBr(e.administradorTelefone) : 'sem telefone'}</span>
                                            </span>
                                            <span className={`lk-cell ${e.cnpj ? 'lk-muted' : 'lk-empty-value'}`}>
                                                <span>{comMascaraCnpj(e.cnpj) || 'sem CNPJ'}</span>
                                            </span>
                                            <span className={`lk-cell ${e.dataCadastro ? 'lk-muted' : 'lk-empty-value'}`}>
                                                <span>{dataCurta(e.dataCadastro) || 'sem data'}</span>
                                            </span>
                                            <span className="lk-cell">
                                                {!data
                                                    ? <span className="lk-empty-value">sem data</span>
                                                    : <span className={dias !== null && dias < 0 ? 'em-vencida' : 'lk-muted'}>
                                                        {data}
                                                        {dias !== null && dias >= 0 && dias <= 30 && (
                                                            <span className="em-prazo"> · {dias}d</span>
                                                        )}
                                                        {dias !== null && dias < 0 && <span> · vencida</span>}
                                                      </span>}
                                            </span>
                                            <span className="lk-cell">
                                                <span className={`rp-badge ${e.ativo === false ? 'is-lost' : 'is-good'}`}>
                                                    {e.ativo === false ? 'Inativa' : 'Ativa'}
                                                </span>
                                            </span>
                                            <RowActions>
                                                <RowAction icon={<LuEye size={14} />} label="Ver empresa"
                                                    onClick={() => abrirVer(e)} />
                                                <RowAction icon={<LuTrash2 size={14} />} label="Desativar empresa" danger
                                                    onClick={() => desativar(e)} />
                                            </RowActions>
                                        </ListRow>
                                    );
                                })}
                        </div>
                    </div>

                    {!isLoading && filtradas.length === 0 && listaVazia}
                    {!isLoading && filtradas.length > 0 && rodape}
                </div>
                )}
            </div>

            {/* As três modais de antes (dados, usuários e assumir controle) viraram
                as três abas deste painel — nada de modal sobre painel. */}
            <RecordPanel
                open={!!aberto}
                onClose={fechar}
                wide
                initials={iniciais(modo === 'editar' ? rascunho.nome : (aberto?.nome ?? ''))}
                title={modo === 'editar'
                    ? (rascunho.nome.trim() || (ehNova ? 'Nova empresa' : 'Empresa'))
                    : (aberto?.nome ?? '')}
                subtitle={aberto && aberto.id
                    ? [comMascaraCnpj(aberto.cnpj), dataCurta(aberto.dataAssinaturaExpira) && `expira ${dataCurta(aberto.dataAssinaturaExpira)}`]
                        .filter(Boolean).join(' · ') || 'Sem CNPJ'
                    : undefined}
                editing={modo === 'editar'}
                tabs={aberto && aberto.id && modo === 'ver' ? [
                    { id: 'dados', label: 'Dados', icon: <LuBuilding2 size={13} /> },
                    { id: 'usuarios', label: 'Usuários', icon: <LuUsers size={13} />, badge: usuarios.length || undefined },
                    { id: 'suporte', label: 'Suporte', icon: <LuLifeBuoy size={13} /> },
                    { id: 'funcionalidades', label: 'Funcionalidades', icon: <LuToggleRight size={13} /> },
                    /* Histórico (fase 6): o da empresa cliente, lido pela GB Code (rota sistema/empresa). */
                    { id: 'historico', label: 'Histórico', icon: <LuHistory size={13} /> },
                ] : undefined}
                activeTab={aba}
                onTabChange={id => setAba(id as Aba)}
                onBack={ehNova ? undefined : () => setModo('ver')}
                footer={modo === 'editar' ? (
                    <>
                        {!ehNova && (
                            <button type="button" className="rp-btn is-danger" onClick={() => desativar()} disabled={salvando}>
                                Desativar
                            </button>
                        )}
                        <button type="button" className="rp-btn" disabled={salvando}
                            onClick={() => (ehNova ? fechar() : setModo('ver'))}>Cancelar</button>
                        <button type="button" className="rp-btn is-primary" onClick={salvar} disabled={salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </>
                ) : undefined}
            >
                {/* ─── Ver: Dados ─────────────────────────────── */}
                {modo === 'ver' && aba === 'dados' && aberto && (
                    <>
                    {botaoEditar}
                    <PanelSection title="Dados">
                        <div className="rp-kv">
                            <span className="rp-k">Nome</span>
                            <span className="rp-v">{aberto.nome}</span>
                            <span className="rp-k">CNPJ</span>
                            <span className={`rp-v ${aberto.cnpj ? '' : 'is-none'}`}>
                                {comMascaraCnpj(aberto.cnpj) || 'não informado'}
                            </span>
                            <span className="rp-k">Administrador</span>
                            <span className={`rp-v ${aberto.administrador ? '' : 'is-none'}`}>
                                {aberto.administrador || 'nenhum ativo'}
                            </span>
                            <span className="rp-k">Cadastro</span>
                            <span className={`rp-v ${aberto.dataCadastro ? '' : 'is-none'}`}>
                                {dataCurta(aberto.dataCadastro) || 'sem data'}
                            </span>
                            <span className="rp-k">Assinatura expira</span>
                            <span className={`rp-v ${aberto.dataAssinaturaExpira ? '' : 'is-none'}`}>
                                {dataCurta(aberto.dataAssinaturaExpira) || 'sem data'}
                            </span>
                            <span className="rp-k">Status</span>
                            <span className="rp-v">{aberto.ativo === false ? 'Inativa' : 'Ativa'}</span>
                        </div>
                    </PanelSection>
                    </>
                )}

                {/* ─── Ver: Usuários ──────────────────────────── */}
                {modo === 'ver' && aba === 'usuarios' && aberto && (
                    <PanelSection title="Usuários da empresa">
                        {carregandoUsuarios ? (
                            <p className="rp-none">Carregando…</p>
                        ) : (
                            <>
                                <div className="em-lista">
                                    {usuarios.map(u => usuarioEmEdicao === u.usuarioId ? (
                                        <div className="em-li is-edit" key={u.usuarioId}>
                                            <div className="em-form">
                                                <input className="rp-input" type="text" placeholder="Nome"
                                                    value={rascunhoUsuario.nome}
                                                    onChange={e => setRascunhoUsuario(r => ({ ...r, nome: e.target.value }))} />
                                                <input className="rp-input" type="email" placeholder="E-mail"
                                                    value={rascunhoUsuario.email}
                                                    onChange={e => setRascunhoUsuario(r => ({ ...r, email: e.target.value }))} />
                                                <Select value={String(rascunhoUsuario.tipoUsuarioId)}
                                                    options={perfis.map(p => ({ value: String(p.id), label: p.nome }))}
                                                    onChange={v => setRascunhoUsuario(r => ({ ...r, tipoUsuarioId: Number(v) }))} />
                                                <label className="em-check">
                                                    <input type="checkbox" checked={rascunhoUsuario.ativo}
                                                        onChange={e => setRascunhoUsuario(r => ({ ...r, ativo: e.target.checked }))} />
                                                    Ativo
                                                </label>
                                                <div className="em-form-acts">
                                                    <button type="button" className="rp-btn" onClick={() => setUsuarioEmEdicao(null)}>Cancelar</button>
                                                    <button type="button" className="rp-btn is-primary" onClick={salvarUsuario} disabled={salvandoUsuario}>
                                                        {salvandoUsuario ? 'Salvando…' : 'Salvar'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="em-li" key={u.usuarioId}>
                                            <span className="em-av">{iniciais(u.nome)}</span>
                                            <span className="em-li-txt">
                                                <span className="em-li-n">{u.nome}</span>
                                                <span className="em-li-e">
                                                    {u.email}{u.telefone && <> · {formatarTelefoneBr(u.telefone)}</>} · {nomePerfil(u.tipoUsuarioId)}
                                                    {u.aceitouMarketingEm && <> · aceita novidades</>}
                                                </span>
                                                {/* Boas-vindas (28/10/2026): só quem criou a conta tem a marca. */}
                                                {u.boasVindas && (
                                                    <span className={`em-li-b ${u.boasVindas === 'concluido' ? 'is-ok' : 'is-pulou'}`}>
                                                        <LuRocket size={12} />
                                                        <span>
                                                            Boas-vindas ·{' '}
                                                            <b>{u.boasVindas === 'concluido' ? 'concluído' : `pulou no passo ${u.boasVindas.replace('pulado-', '')}`}</b>
                                                            {u.boasVindasEm && <> em {formatarDataCurta(u.boasVindasEm)}</>}
                                                        </span>
                                                    </span>
                                                )}
                                            </span>
                                            <span className={`rp-badge ${!u.ativo ? 'is-lost' : u.conviteExpirado ? 'is-warn' : u.convitePendente ? 'is-open' : 'is-good'}`}>
                                                {!u.ativo ? 'Inativo' : u.conviteExpirado ? 'Convite expirado' : u.convitePendente ? 'Convite pendente' : 'Ativo'}
                                            </span>
                                            <button type="button" className="em-ic" aria-label={`Editar ${u.nome}`}
                                                onClick={() => abrirUsuario(u)}>
                                                <LuPencil size={13} />
                                            </button>
                                        </div>
                                    ))}

                                    {usuarioEmEdicao === 'novo' && (
                                        <div className="em-li is-edit">
                                            <div className="em-form">
                                                <input className="rp-input" type="text" placeholder="Nome" autoFocus
                                                    value={rascunhoUsuario.nome}
                                                    onChange={e => setRascunhoUsuario(r => ({ ...r, nome: e.target.value }))} />
                                                <input className="rp-input" type="email" placeholder="E-mail"
                                                    value={rascunhoUsuario.email}
                                                    onChange={e => setRascunhoUsuario(r => ({ ...r, email: e.target.value }))} />
                                                <Select value={String(rascunhoUsuario.tipoUsuarioId)}
                                                    options={perfis.map(p => ({ value: String(p.id), label: p.nome }))}
                                                    onChange={v => setRascunhoUsuario(r => ({ ...r, tipoUsuarioId: Number(v) }))} />
                                                <div className="em-form-acts">
                                                    <button type="button" className="rp-btn" onClick={() => setUsuarioEmEdicao(null)}>Cancelar</button>
                                                    <button type="button" className="rp-btn is-primary" onClick={salvarUsuario} disabled={salvandoUsuario}>
                                                        {salvandoUsuario ? 'Enviando…' : 'Enviar convite'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {usuarios.length === 0 && usuarioEmEdicao !== 'novo' && (
                                        <p className="rp-none">Esta empresa ainda não tem usuários.</p>
                                    )}
                                </div>

                                {usuarioEmEdicao !== 'novo' && (
                                    <button type="button" className="rp-add" onClick={() => abrirUsuario()}>
                                        <LuPlus size={13} /> Convidar usuário
                                    </button>
                                )}
                            </>
                        )}
                    </PanelSection>
                )}

                {/* ─── Ver: Funcionalidades ───────────────────── */}
                {modo === 'ver' && aba === 'funcionalidades' && aberto && (() => {
                    if (carregandoFuncionalidades) return <PanelSection title="Funcionalidades"><p className="rp-none">Carregando…</p></PanelSection>;
                    if (!funcionalidades) return <PanelSection title="Funcionalidades"><p className="rp-none">Não foi possível carregar.</p></PanelSection>;
                    const linhas = funcionalidades.funcionalidades.map(f => ({ f, ...acessoDaEmpresa(f, aberto.id) }));
                    const tem = linhas.filter(l => l.tem).length;
                    return (
                        <PanelSection title={`${tem} de ${linhas.length} funcionalidades`}>
                            {linhas.map(({ f, tem: temEsta, fonte }) => (
                                <div key={f.chave} className="em-fn-l">
                                    <button type="button" className="em-fn-nome" title={`Abrir ${f.nome} em Funcionalidades`}
                                        onClick={() => navigate(`/sistema/funcionalidades?chave=${encodeURIComponent(f.chave)}`)}>
                                        {f.nome}
                                    </button>
                                    <span className={`sis-fn-st ${temEsta ? 'is-ligada' : 'is-desligada'}`}><i />{temEsta ? 'tem' : 'não tem'}</span>
                                    <span className={`em-fn-fonte ${fonte === 'excecao' ? 'is-excecao' : ''}`}>
                                        {fonte === 'global' ? 'pelo global' : fonte === 'excecao' ? 'exceção' : 'exceção sem efeito'}
                                    </span>
                                </div>
                            ))}
                        </PanelSection>
                    );
                })()}

                {/* ─── Ver: Suporte ───────────────────────────── */}
                {modo === 'ver' && aba === 'suporte' && aberto && (
                    <PanelSection title="Assumir o controle">
                        <p className="em-aviso">
                            <LuTriangleAlert size={14} />
                            <span>
                                Você passa a usar a plataforma <b>como o usuário escolhido</b>, dentro de
                                "{aberto.nome}". Sua sessão atual é trocada e você vai para o Fluxo de Vendas.
                                Para voltar, saia e entre de novo.
                            </span>
                        </p>

                        {carregandoAdmins ? (
                            <p className="rp-none">Carregando administradores…</p>
                        ) : admins.length === 0 ? (
                            /* Sem admin ativo não há como entrar: é regra da API, não falha de tela. */
                            <p className="rp-none">
                                Esta empresa não tem nenhum administrador ativo — não é possível assumir o controle.
                            </p>
                        ) : (
                            <div className="em-lista">
                                {admins.map(a => (
                                    <div className="em-li" key={a.userId}>
                                        <span className="em-av">{iniciais(a.userName)}</span>
                                        <span className="em-li-txt">
                                            <span className="em-li-n">{a.userName}</span>
                                            <span className="em-li-e">{a.userEmail}</span>
                                        </span>
                                        <button type="button" className="rp-btn" disabled={assumindo}
                                            onClick={() => assumir(a)}>
                                            Entrar como
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </PanelSection>
                )}

                {/* ─── Editar ─────────────────────────────────── */}
                {modo === 'editar' && (
                    <PanelSection title="Dados da empresa">
                        <div className="rp-field">
                            <label className="rp-label" htmlFor="em-nome">Nome</label>
                            <input id="em-nome" className="rp-input" type="text" autoFocus
                                value={rascunho.nome}
                                onChange={e => { setRascunho(r => ({ ...r, nome: e.target.value })); setErros({ ...erros, nome: '' }); }} />
                            {erros.nome && <span className="rp-error">{erros.nome}</span>}
                        </div>

                        <div className="rp-two">
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="em-cnpj">CNPJ</label>
                                <input id="em-cnpj" className="rp-input" type="text" inputMode="numeric"
                                    value={comMascaraCnpj(rascunho.cnpj)} placeholder="00.000.000/0000-00"
                                    onChange={e => { setRascunho(r => ({ ...r, cnpj: soDigitos(e.target.value) })); setErros({ ...erros, cnpj: '' }); }} />
                                {erros.cnpj && <span className="rp-error">{erros.cnpj}</span>}
                            </div>
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="em-exp">Assinatura expira em</label>
                                <input id="em-exp" className="rp-input" type="date"
                                    value={rascunho.expira}
                                    onChange={e => setRascunho(r => ({ ...r, expira: e.target.value }))} />
                            </div>
                        </div>
                    </PanelSection>
                )}

                {aba === 'historico' && modo === 'ver' && aberto?.id ? (
                    <SecaoHistorico entidade="empresa" id={aberto.id} fonte="sistema-empresa" />
                ) : null}
            </RecordPanel>
        </div>
    );
};

export default EmpresasPage;
